/**
 * Motor "seriar" — ordenar por tamanho ou valor.
 *
 * A crianca arrasta os elementos para os lugares da fileira, do menor para o
 * maior. D13: em "tamanho" a diferenca e visivel no proprio objeto; nao ha
 * numeral nenhum envolvido.
 * D6: ordem trocada nao e erro. So conclui quando estiver em ordem, e a crianca
 *     pode remexer a vontade ate chegar la.
 */

import * as THREE from 'three';
import { BaseEngine } from './BaseEngine.js';

export class SeriarEngine extends BaseEngine {
  constructor(ctx) {
    super(ctx);
    this.lugares = [];
    this.elementos = [];
    this.grupo = null;
  }

  async iniciar(tarefa, banda) {
    await super.iniciar(tarefa, banda);

    const payload = tarefa.bandas[String(banda)];
    if (!payload) {
      throw new Error(`FAZENDA: a tarefa "${tarefa.id}" nao tem payload para a banda ${banda}.`);
    }

    this.quantidade = payload.elementos;
    this.atributo = payload.atributo; // "tamanho" | "quantidade"

    this.montar();
    console.info(
      `FAZENDA: tarefa "${tarefa.id}" iniciada — banda ${banda}, ` +
        `${this.quantidade} elementos por ${this.atributo}.`
    );
  }

  montar() {
    const { fazenda, sceneManager, input } = this.ctx;

    this.grupo = new THREE.Group();
    this.grupo.name = 'tarefa-seriar';
    const base = fazenda.posicaoDaAncora(this.tarefa.ancora);
    const largura = (this.quantidade - 1) * 1.5;

    // Lugares da fileira, na ordem certa da esquerda para a direita.
    for (let i = 0; i < this.quantidade; i++) {
      const lugar = new THREE.Mesh(
        new THREE.BoxGeometry(1.2, 0.1, 1.2),
        new THREE.MeshStandardMaterial({
          color: 0x8d6e63, roughness: 1, transparent: true, opacity: 0.6, flatShading: true,
        })
      );
      lugar.position.set(base.x - largura / 2 + i * 1.5, base.y + 0.06, base.z + 2.6);
      lugar.userData.ordem = i;
      lugar.userData.ocupadoPor = null;
      this.grupo.add(lugar);
      this.lugares.push(lugar);
    }

    // Elementos embaralhados, com tamanhos claramente diferentes.
    const ordens = embaralhar([...Array(this.quantidade).keys()]);
    ordens.forEach((ordem, i) => {
      const escala = 0.4 + ordem * (1.1 / this.quantidade);
      const elemento = new THREE.Mesh(
        new THREE.CylinderGeometry(escala * 0.5, escala * 0.55, escala * 1.6, 8),
        new THREE.MeshStandardMaterial({ color: 0x66bb6a, roughness: 1, flatShading: true })
      );
      elemento.position.set(base.x - largura / 2 + i * 1.5, base.y + escala * 0.8, base.z - 0.4);
      elemento.userData.ordem = ordem;
      elemento.userData.noLugar = null;
      elemento.userData.alturaBase = base.y + escala * 0.8;

      this.grupo.add(elemento);
      this.elementos.push(elemento);

      input.registrarArrastavel(elemento, {
        destinos: () => this.lugares,
        aoSoltar: (objeto, lugar) => this.aoSoltarElemento(objeto, lugar),
      });
    });

    sceneManager.adicionar(this.grupo);
    input.conferirTamanhoDeAlvo(this.lugares[0], `lugar da tarefa ${this.tarefa.id}`);
  }

  aoSoltarElemento(elemento, lugar) {
    const { audio } = this.ctx;

    if (elemento.userData.noLugar) {
      elemento.userData.noLugar.userData.ocupadoPor = null;
      elemento.userData.noLugar = null;
    }

    if (!lugar || lugar.userData.ocupadoPor) {
      audio.tocarTentarDeNovo(); // D6
      return;
    }

    lugar.userData.ocupadoPor = elemento;
    elemento.userData.noLugar = lugar;
    elemento.position.set(lugar.position.x, elemento.userData.alturaBase, lugar.position.z);

    audio.tocarToque(lugar.userData.ordem);
    if (this.concluiu()) audio.tocarSucesso();
  }

  /** Em ordem quando cada lugar tem o elemento da ordem correspondente. */
  concluiu() {
    return this.lugares.every((l) => l.userData.ocupadoPor?.userData.ordem === l.userData.ordem);
  }

  atualizar(dt) {
    for (const e of this.elementos) {
      if (!e.userData.noLugar) e.rotation.y += dt * 0.9;
    }
  }

  async concluir() {
    return super.concluir({ concluiu: this.concluiu() });
  }

  descartar() {
    const { input, sceneManager } = this.ctx;
    for (const e of this.elementos) input.desregistrar(e);
    if (this.grupo) {
      sceneManager.remover(this.grupo);
      this.grupo.traverse((o) => { o.geometry?.dispose(); o.material?.dispose(); });
    }
    this.lugares = [];
    this.elementos = [];
    this.grupo = null;
    super.descartar();
  }
}

function embaralhar(lista) {
  for (let i = lista.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [lista[i], lista[j]] = [lista[j], lista[i]];
  }
  return lista;
}
